import { useCallback, useState } from 'react';

import { DeleteForever, Edit } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip } from '@mui/material';

import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SecretAndVariableModal from '../../components/modals/SecretAndVariableModal';
import DataTable from '../../components/tables/DataTable';
import { Column } from '../../components/tables/interfaces';
import { axiosRepoIntance } from '../../services/api-client';
import { ActionVariable } from '../../services/repo-service';
import { OWNER } from '../../utils/constants/gitea';
import { AddSecretAndVariableForm } from '../../utils/models/secrets-and-variables';


const VariablesList: React.FC = () => {

    const columns: Column[] = [
        { id: "name", label: "Name", type: 'string' },
        { id: "data", label: "Value", type: 'string' },
        { id: "action", label: "Actions", },
    ];

    const [addVariableFormData, setAddVariableFormData] = useState<AddSecretAndVariableForm>({ name: '', value: '' });
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [deleteSelectedVariable, setdeleteSelectedVariable] = useState<string>('');
    const [isAddMode, setIsAddMode] = useState<boolean>(true);
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const handleAddClick = () => {
        setIsAddMode(true);
        setAddVariableFormData({ name: '', value: '' });
        setOpenAddModal(true)
    }

    const handleAddVariableForm = (form_data: AddSecretAndVariableForm) => {
        console.log('add secret form data: ', form_data);
        createRepoActionVariable(isAddMode, form_data);
        setAddVariableFormData({ name: '', value: '' });
    }

    const handleDeleteVariable = (isDelete: boolean) => {
        if (!isDelete) return;

        deleteActionVariable(deleteSelectedVariable);
        setdeleteSelectedVariable('');
    }

    const listRepoActionVariables = async (query?: { page?: number; limit?: number }) => {
        try {
            // const response = await axiosRepoIntance.repos.getRepoVariablesList(OWNER, repo_name);
            const response = await axiosRepoIntance.orgs.getOrgVariablesList(OWNER);
            // console.log('listRepoActionvariable', respone.data);
            // setVariablesList(respone.data);
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return alert('variables not found');
            }
            console.log('error while fetching variable list: ', error)
        }
    }

    const createRepoActionVariable = async (isCreateMode: boolean, updateData: AddSecretAndVariableForm) => {
        try {
            let response;
            if (isCreateMode) {
                // response = await axiosRepoIntance.repos.createRepoVariable(OWNER, selectedRepo, updateData.name, { value: updateData.value });
                response = await axiosRepoIntance.orgs.createOrgVariable(OWNER, updateData.name, { value: updateData.value })
            }
            else {
                // response = await axiosRepoIntance.repos.updateRepoVariable(OWNER, selectedRepo, updateData.name, { value: updateData.value });
                response = await axiosRepoIntance.orgs.updateOrgVariable(OWNER, updateData.name, { value: updateData.value })
            }
            console.log('create/update variable: ', response);
            if (response.status === 201 || response.status === 204) {
                setRefreshKey(prevKey => prevKey + 1);
            }
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.status === 404) {
                return alert(error?.response?.data?.message)
            } else if (error?.response?.status === 422) {
                return alert(error?.response?.data?.message)
            }
            console.log('error while create/update variable: ', error);
        }
    }

    const getRepoActionVariable = async (var_name: string) => {
        try {
            // const response = await axiosRepoIntance.repos.getRepoVariable(OWNER, selectedRepo, var_name);
            const response = await axiosRepoIntance.orgs.getOrgVariable(OWNER, var_name);
            console.log('get variable; ', response)
            if (response.status === 200) {
                return response.data;
            }
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.status === 404) {
                return alert(error?.response?.data?.message)
            } else if (error?.response?.status === 422) {
                return alert(error?.response?.data?.message)
            }
            console.log('error while getting a variable: ', error);
            return null;
        }
    }

    const deleteActionVariable = async (name: string) => {
        try {
            // const response = await axiosRepoIntance.repos.deleteRepoVariable(OWNER, selectedRepo, name)
            const response = await axiosRepoIntance.orgs.deleteOrgVariable(OWNER, name);
            console.log('delete variable response: ', response);
            if (response.status === 201 || response.status === 204) {
                setRefreshKey(prevKey => prevKey + 1);
            }
        } catch (error: any) {
            if (error?.response?.status === 400 || error?.response?.status === 404) {
                return alert(error?.response?.data?.message)
            } else if (error?.response?.status === 422) {
                return alert(error?.response?.data?.message)
            }
            console.log('error while delete variable: ', error)
        }
    }

    const fetchData = useCallback(async (query?: { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => {
        const response = await listRepoActionVariables(query as { page?: number; limit?: number })
        console.log('fetched table data: ', response)
        return response || [];
    }, [])


    const dataExtractor = useCallback((response: ActionVariable[]) => {
        return response.map(item => ({
            id: item.name,
            name: item.name,
            data: item.data,
            action: <Box display='flex' gap={1}>
                <Tooltip title='Edit Variable'>
                    <IconButton color='warning' onClick={async () => {
                        setIsAddMode(false);

                        const response_data = await getRepoActionVariable(item.name || '')
                        if (!response_data || !response_data.data || !item.name) return;

                        setAddVariableFormData({ name: item.name || '', value: response_data.data });
                        setOpenAddModal(true);
                    }}>
                        <Edit />
                    </IconButton>
                </Tooltip>
                <Tooltip title='Delete Variable' onClick={() => {
                    setOpenDeleteModal(true)
                    setdeleteSelectedVariable(item.name || '')
                }}>
                    <IconButton color='error'><DeleteForever /></IconButton>
                </Tooltip>
            </Box>
        }))
    }, [setOpenAddModal])

    return (
        <Box>
            <Box sx={{ m: 2, display: 'flex', justifySelf: 'flex-end' }} >
                <Button variant='contained' onClick={handleAddClick}>
                    add variable
                </Button>
            </Box>

            <Box key={refreshKey}>
                <DataTable
                    columns={columns}
                    fetchData={fetchData}
                    dataExtractor={dataExtractor}
                />

                {openAddModal &&
                    <SecretAndVariableModal
                        open={openAddModal}
                        formData={addVariableFormData}
                        headingTitle='Variable'
                        setOpen={setOpenAddModal}
                        handleDataSubmit={handleAddVariableForm}
                    />
                }

                {openDeleteModal &&
                    <ConfirmationModal
                        open={openDeleteModal}
                        setOpen={setOpenDeleteModal}
                        headTitle='Delete'
                        bodyMessage='Removing a secret is permanent and cannot be undone. Continue?'
                        confirmBtnColor='error'
                        handleSubmit={handleDeleteVariable}
                    />
                }
            </Box>
        </Box>
    )
}

export default VariablesList